import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { transparentHexColor } from '@omni/kit/utilities/utilities';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  GestureResponderEvent,
  Platform,
  Pressable,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';

interface VolumeSliderProps {
  volume?: number;
  onChange?: (value: number) => void;
  sliderContainerStyle?: ViewStyle;
  sliderStyle?: ViewStyle;
}

export default function VolumeSlider({
  volume = 1,
  onChange,
  sliderContainerStyle,
  sliderStyle,
}: VolumeSliderProps): ReactElement {
  const [isDragging, setIsDragging] = useState(false);
  const [dragPercentage, setDragPercentage] = useState(volume * 100);
  const containerRef = useRef<View>(null);

  useEffect(() => {
    setDragPercentage(volume * 100);
  }, [volume]);

  const handleDrag = useCallback(
    (positionY: number, sliderHeight: number) => {
      const clampedY = Math.max(0, Math.min(sliderHeight, positionY));
      const newPercentage = 100 - (clampedY / sliderHeight) * 100;
      setDragPercentage(newPercentage);
      onChange?.(newPercentage / 100);
    },
    [onChange]
  );

  useEffect(() => {
    const handleGlobalMouseMove = (event: MouseEvent) => {
      if (isDragging && containerRef.current) {
        containerRef.current.measure((_, __, ___, height, ____, pageY) => {
          const pointerY = event.pageY - pageY;
          handleDrag(pointerY, height);
        });
      }
    };

    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
      }
    };

    if (Platform.OS === 'web') {
      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      if (Platform.OS === 'web') {
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        window.removeEventListener('mouseup', handleGlobalMouseUp);
      }
    };
  }, [isDragging, handleDrag]);

  const handlePressIn = useCallback(
    (event: GestureResponderEvent) => {
      if (containerRef.current) {
        containerRef.current.measure((_, __, ___, height, ____, pY) => {
          const touchY = event.nativeEvent.pageY - pY;
          handleDrag(touchY, height);
          setIsDragging(true);
        });
      }
    },
    [handleDrag]
  );

  const handleMouseDown = useCallback(
    (event: MouseEvent) => {
      if (containerRef.current) {
        containerRef.current.measure((_, __, ___, height, ____, pY) => {
          const touchY = event.pageY - pY;
          handleDrag(touchY, height);
          setIsDragging(true);
        });
      }
    },
    [handleDrag]
  );

  const handlePressOut = () => {
    setIsDragging(false);
  };

  return (
    <Pressable
      ref={containerRef}
      style={[styles.container, sliderContainerStyle]}
      onPressIn={Platform.OS === 'web' ? undefined : handlePressIn}
      onPressOut={Platform.OS === 'web' ? undefined : handlePressOut}
      {...(Platform.OS === 'web' ? { onMouseDown: handleMouseDown } : {})}
    >
      <View style={styles.barContainer}>
        <View
          style={[styles.bar, sliderStyle, { height: `${dragPercentage}%` }]}
        />
      </View>
      <View style={[styles.handle, { bottom: `${dragPercentage}%` }]} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 40,
    height: 150,
    alignItems: 'center',
    justifyContent: 'center',
  },
  barContainer: {
    width: Spacing.xs,
    height: '100%',
    borderRadius: Spacing.l,
    backgroundColor: transparentHexColor(Colors.N0, 40),
    position: 'relative',
  },
  bar: {
    width: Spacing.xs,
    borderRadius: Spacing.l,
    position: 'absolute',
    bottom: 0,
  },
  handle: {
    width: Spacing.l,
    height: Spacing.l,
    borderRadius: Spacing.l / 2,
    backgroundColor: Colors.N0,
    position: 'absolute',
    zIndex: 1,
    marginBottom: -Spacing.l / 2,
  },
});
